import React, { useRef, useState, useEffect, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import BucketRangeButtons from './BucketRangeButtons';
import {
  TooltipComponent,
  TitleComponent,
  GridComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import dayjs from 'dayjs';
import axios from 'axios';
import { FallingLines } from 'react-loader-spinner';
import { useAppContext } from '../../../providers/AppProvider';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TooltipComponent,
  TitleComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);

const getDefaultOptions = (
  theme,
  getThemeColor,
  activeTimeData,
  breakTimeData
) => ({
  color: [getThemeColor('primary'), getThemeColor('secondary')],
  tooltip: {
    trigger: 'axis',
    padding: 10,
    backgroundColor: getThemeColor('gray-100'),
    borderColor: getThemeColor('gray-300'),
    textStyle: { color: getThemeColor('dark') },
    borderWidth: 1,
    transitionDuration: 0,
    axisPointer: {
      type: 'shadow'
    },
    formatter: params => {
      let activeTime, breakTime;
      // Check that params includes both series (e.g., params[1] for ACTIVE_TIME, params[2] for BREAK_TIME)
      const time = dayjs(params[0].value[0]).format('HH:mm:ss');
      if (params.length === 2) {
        activeTime = params[0]
          ? `${params[0].seriesName}: ${params[0].value[1]} minutes`
          : '';
        breakTime = params[1]
          ? `${params[1].seriesName}: ${params[1].value[1]} minutes`
          : '';
      } else {
        activeTime = params[1]
          ? `${params[1].seriesName}: ${params[1].value[1]} minutes`
          : '';
        breakTime = params[2]
          ? `${params[2].seriesName}: ${params[2].value[1]} minutes`
          : '';
      }

      return `Time: ${time}<br/>${activeTime}<br/>${breakTime}`;
    }
  },
  xAxis: {
    type: 'time',
    // boundaryGap: false,
    axisLabel: {
      color: getThemeColor('gray-800'),
      interval: 3,
      fontFamily: 'Nunito Sans',
      fontWeight: 600,
      fontSize: 12.8
    },
    axisLine: {
      lineStyle: {
        color: getThemeColor('gray-300')
      }
    },
    axisTick: false
  },
  yAxis: {
    type: 'value',
    axisLine: { lineStyle: { color: getThemeColor('gray-300') } },
    splitLine: { show: true, lineStyle: { color: getThemeColor('gray-300') } },
    axisLabel: { color: getThemeColor('gray-900'), formatter: '{value} min' }
  },
  series: [
    {
      name: 'Active Time',
      type: 'bar',
      data: activeTimeData.map(([time, value]) => [time, value / 60]), // Convert seconds to minutes
      barWidth: '6px',
      barGap: '30%',
      label: { show: false },
      itemStyle: { borderRadius: [2, 2, 0, 0], color: getThemeColor('primary') }
    },
    {
      name: 'Break Time',
      type: 'bar',
      data: breakTimeData.map(([time, value]) => [time, value / 60]), // Convert seconds to minutes
      barWidth: '12px',
      barGap: '30%',
      label: { show: false },
      z: 10,
      itemStyle: {
        borderRadius: [2, 2, 0, 0],
        color: getThemeColor('info-100')
      }
    }
  ],
  grid: {
    right: 40,
    left: 20,
    bottom: '20px',
    top: '10%',
    containLabel: true
  },
  animation: true,
  dataZoom: [
    {
      type: 'inside',
      xAxisIndex: 0,
      start: 0,
      end: 100
    }
  ]
});

const ActiveBreakTimeBarChart = ({
  bucket,
  dynamicBucket = false,
  machineName,
  fields,
  startTime,
  endTime,
  title
}) => {
  const chartRef = useRef(null);
  const [activeTimeData, setActiveTimeData] = useState([]);
  const [breakTimeData, setBreakTimeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBucket, setCurrentBucket] = useState(bucket);

  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${currentBucket}`
      );
      const data = response.data || {};

      const activeTime = data.ACTIVE_TIME || [];
      const breakTime = data.BREAK_TIME || [];

      setActiveTimeData(activeTime);
      setBreakTimeData(breakTime);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBucket = bucketName => setCurrentBucket(bucketName);

  const getIntervalTime = () => {
    return 60 * 1000;
  };

  useEffect(() => {
    fetchData();
    const intervalTime = getIntervalTime();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, intervalTime) : null;
    return () => clearInterval(intervalId);
  }, [currentBucket, startTime, endTime]);

  if (loading) {
    return (
      <Card className="h-0">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
          {dynamicBucket && (
            <BucketRangeButtons
              current={currentBucket}
              onChange={updateBucket}
            />
          )}
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getDefaultOptions(
            theme,
            getThemeColor,
            activeTimeData,
            breakTimeData
          )}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    // minWidth lets a long title wrap instead of shoving the resolution
    // picker out of the card: a flex item will not shrink below its
    // content width until it is told it may.
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.25,
    paddingRight: '8px'
  },
};

export default memo(ActiveBreakTimeBarChart);
